import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles, ArrowRight, Shield, UserCog, Building2 } from "lucide-react";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Inkubera — Portfolio Intelligence" },
      {
        name: "description",
        content:
          "Privat portföljportal för Inkubera. Inloggning för personal, coacher och portföljbolag.",
      },
    ],
  }),
  component: Landing,
});

const portals = [
  {
    to: "/login",
    title: "Inkubera-personal",
    desc: "Affärsutvecklare, styrelse och administratörer. Full åtkomst till portföljen, pipeline, rapporter och coachtilldelning.",
    icon: Shield,
    badge: "Personal",
  },
  {
    to: "/coach-login",
    title: "Affärscoach",
    desc: "Inloggning för externa coacher. Du ser bara de bolag du är tilldelad och lämnar in månadsrapporter.",
    icon: UserCog,
    badge: "Coach",
  },
  {
    to: "/bolag-login",
    title: "Portföljbolag",
    desc: "Inloggning för portföljbolag. Du ser din bolagsprofil och de månadsrapporter din coach lämnat in.",
    icon: Building2,
    badge: "Bolag",
  },
] as const;

function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <header className="px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="size-9 rounded-xl bg-foreground flex items-center justify-center text-background">
            <Sparkles className="size-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-lg leading-none">Inkubera</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-0.5">
              Portfolio Intelligence
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 px-8 py-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-xs uppercase tracking-[0.22em] text-muted-foreground mb-4">
            Privat portal · Endast inbjudna
          </div>
          <h1 className="font-display text-5xl md:text-6xl leading-[1.05] tracking-tight max-w-3xl">
            Strategisk översikt över Inkuberas bolagsportfölj.
          </h1>
          <p className="mt-6 text-muted-foreground text-lg max-w-2xl">
            Välj inloggning. Olika roller ger olika behörighet — personal ser hela portföljen,
            coacher ser sina tilldelade bolag, och portföljbolag ser sin egen profil.
          </p>

          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {portals.map((p) => (
              <Link
                key={p.to}
                to={p.to}
                className="group rounded-2xl border border-border bg-card p-6 hover:border-foreground/40 transition-colors flex flex-col"
              >
                <div className="flex items-center justify-between">
                  <div className="size-10 rounded-xl bg-secondary flex items-center justify-center">
                    <p.icon className="size-5" />
                  </div>
                  <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                    {p.badge}
                  </span>
                </div>
                <h2 className="mt-5 font-display text-xl">{p.title}</h2>
                <p className="mt-2 text-sm text-muted-foreground flex-1">{p.desc}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-sm font-medium">
                  Logga in
                  <ArrowRight className="size-4 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-10 text-xs text-muted-foreground">
            Behöver du åtkomst? Kontakta din administratör på Inkubera.
          </div>
        </div>
      </main>

      <footer className="px-8 py-6 text-xs text-muted-foreground border-t border-border">
        © {new Date().getFullYear()} Inkubera i Örebro
      </footer>
    </div>
  );
}
